import React, { useState, useEffect } from 'react';
import { X, Save, Edit2, Trash2, Package, AlertCircle } from 'lucide-react';
import axios from 'axios';

import API_BASE_URL from "../config";

export default function PurchaseOrderEditModal({ order, onClose, onSave, companies, vendors }) {
  const [editFormData, setEditFormData] = useState({});
  const [poDetails, setPoDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Shipping locations state
  const [shippingLocations, setShippingLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch PO Details
        const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/get/${order.id}`);
        const detailsData = await detailsRes.json();
        setPoDetails(detailsData);

        // ✅ Fetch Shipping Locations
        const shippingRes = await axios.get(`${API_BASE_URL}api/ShippingInfo`);
        const activeLocations = shippingRes.data.filter(info => info.isActive === 1);
        setShippingLocations(activeLocations);
        setLoadingLocations(false);

        // Set form data
        setEditFormData({
          ...order,
          poCreatedDate: formatDateForInput(order.poCreatedDate),
          deliveryDate: formatDateForInput(order.deliveryDate),
        });
      } catch (error) {
        console.error('Error fetching PO data:', error);
        setLoadingLocations(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [order.id]);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // ✅ Handle location selection with auto-fill
  const handleLocationSelect = (locationName) => {
    setEditFormData(prev => ({ ...prev, shipTo_Location: locationName }));

    const selected = shippingLocations.find(info => info.shipTo_Location === locationName);
    if (selected) {
      setEditFormData(prev => ({
        ...prev,
        shipTo_Location: locationName,
        shipTo_Address1: selected.shipTo_Address1 || "",
        shipTo_Address2: selected.shipTo_Address2 || "",
        shipTo_ContactPerson: selected.shipTo_ContactName || "",
        shipTo_ContactNo: selected.shipTo_ContactNo || ""
      }));
    }
  };

  const handleSave = async () => {
    try {
      // Save all PO Details first
      await Promise.all(poDetails.map(detail =>
        fetch(`${API_BASE_URL}api/T_Po_Details/${detail.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...detail, id: detail.id })
        })
      ));

      const response = await fetch(`${API_BASE_URL}api/T_PO_Header/${order.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...editFormData,
          id: order.id,
        })
      });

      if (response.ok) {
        const user = JSON.parse(localStorage.getItem("user"));
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "UPDATE",
            module: "Purchase Order",
            description: `PO ${order.poNumber} was updated. PO ID: ${order.id}`,
            userId: user?.userId,
            username: user?.username,
            fullName: user?.fullName || user?.username,
          })
        }).catch(err => console.error("Log failed:", err));
        // SUCCESS MODAL
        const successModal = document.createElement('div');
        successModal.innerHTML = `
          <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000;">
            <div style="background: white; padding: 32px; border-radius: 16px; max-width: 400px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
              <div style="width: 64px; height: 64px; background: #D1FAE5; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
                <svg style="width: 32px; height: 32px; color: #10B981;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </div>
              <h3 style="font-size: 24px; font-weight: bold; color: #1F2937; margin-bottom: 8px;">Success!</h3>
              <p style="color: #6B7280; margin-bottom: 24px;">Purchase Order updated successfully!</p>
              <button id="ok-btn" style="padding: 12px 24px; background: #10B981; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; width: 100%;">OK</button>
            </div>
          </div>
        `;

        document.body.appendChild(successModal);

        successModal.querySelector('#ok-btn').onclick = () => {
          document.body.removeChild(successModal);
          onSave();
          onClose();
        };

        setTimeout(() => {
          if (document.body.contains(successModal)) {
            document.body.removeChild(successModal);
            onSave();
            onClose();
          }
        }, 2000);
      } else {
        throw new Error('Failed to update');
      }
    } catch (error) {
      console.error('Error updating PO:', error);

      // ERROR MODAL
      const errorModal = document.createElement('div');
      errorModal.innerHTML = `
        <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000;">
          <div style="background: white; padding: 32px; border-radius: 16px; max-width: 400px; text-align: center; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
            <div style="width: 64px; height: 64px; background: #FEE2E2; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px;">
              <svg style="width: 32px; height: 32px; color: #EF4444;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
            <h3 style="font-size: 24px; font-weight: bold; color: #1F2937; margin-bottom: 8px;">Error!</h3>
            <p style="color: #6B7280; margin-bottom: 24px;">Failed to update Purchase Order</p>
            <button id="ok-btn" style="padding: 12px 24px; background: #EF4444; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; width: 100%;">OK</button>
          </div>
        </div>
      `;

      document.body.appendChild(errorModal);

      errorModal.querySelector('#ok-btn').onclick = () => {
        document.body.removeChild(errorModal);
      };
    }
  };

  const handleUpdateDetail = async (detailId, updatedData) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/T_Po_Details/${detailId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...updatedData,
          id: detailId,
        })
      });

      if (response.ok) {
        // Refresh details
        const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/get/${order.id}`);
        const detailsData = await detailsRes.json();
        setPoDetails(detailsData);

        // Success notification
        alert('Detail updated successfully!');
      } else {
        alert('Failed to update detail');
      }
    } catch (error) {
      console.error('Error updating detail:', error);
      alert('Error updating detail');
    }
  };

  const handleDeleteDetail = async (detailId) => {
    try {
      const response = await fetch(`${API_BASE_URL}api/T_Po_Details/${detailId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Refresh details
        const detailsRes = await fetch(`${API_BASE_URL}api/T_Po_Details/get/${order.id}`);
        const detailsData = await detailsRes.json();
        setPoDetails(detailsData);

        alert('Detail deleted successfully!');
      } else {
        alert('Failed to delete detail');
      }
    } catch (error) {
      console.error('Error deleting detail:', error);
      alert('Error deleting detail');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 shadow-2xl">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-gray-700 font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl my-8">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white rounded-t-3xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Edit2 size={28} />
              <div>
                <h2 className="text-2xl font-bold">Edit Purchase Order</h2>
                <p className="text-blue-100 text-sm">{order.poNumber}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="space-y-6">
            {/* Edit Form for PO Header */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4">Purchase Order Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">PO Number</label>
                  <input
                    type="text"
                    value={editFormData.poNumber || ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
                  <select
                    value={editFormData.companyId || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, companyId: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Company</option>
                    {Object.entries(companies).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Vendor</label>
                  <select
                    value={editFormData.vendorId || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, vendorId: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Vendor</option>
                    {Object.entries(vendors).map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">PO Created Date</label>
                  <input
                    type="date"
                    disabled
                    value={editFormData.poCreatedDate || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, poCreatedDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Date</label>
                  <input
                    type="date"
                    value={editFormData.deliveryDate || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, deliveryDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Terms</label>
                  <input
                    type="text"
                    value={editFormData.paymentTerms || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, paymentTerms: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* ✅ Ship To Location - DROPDOWN */}
                <div className="md:col-span-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Ship To Location</label>
                  <select
                    value={editFormData.shipTo_Location || ''}
                    onChange={(e) => handleLocationSelect(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select a location</option>
                    {loadingLocations ? (
                      <option>Loading...</option>
                    ) : shippingLocations.length === 0 ? (
                      <option>No locations available</option>
                    ) : (
                      shippingLocations.map((loc) => (
                        <option key={loc.shipTo_Location} value={loc.shipTo_Location}>
                          {loc.shipTo_Location}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Address 1</label>
                  <input
                    type="text"
                    value={editFormData.shipTo_Address1 || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, shipTo_Address1: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>



                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Person</label>
                  <input
                    type="text"
                    value={editFormData.shipTo_ContactPerson || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, shipTo_ContactPerson: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Contact No</label>
                  <input
                    type="text"
                    value={editFormData.shipTo_ContactNo || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, shipTo_ContactNo: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editFormData.isVatInclusive || false}
                      disabled
                      onChange={(e) => setEditFormData({ ...editFormData, isVatInclusive: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-semibold text-gray-700">VAT Inclusive</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editFormData.isNonVAT || false}
                      disabled
                      onChange={(e) => setEditFormData({ ...editFormData, isNonVAT: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-semibold text-gray-700">Non-VAT</span>
                  </label>
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Remarks</label>
                  <textarea
                    value={editFormData.remarks || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Edit PO Details */}
            {/* Edit PO Details — inline table, lahat sabay */}
            <div>
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Package size={20} />
                Items ({poDetails.length})
              </h3>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 text-left">Item Description</th>
                      <th className="px-4 py-3 text-left">Qty</th>
                      <th className="px-4 py-3 text-left">UOM</th>
                      <th className="px-4 py-3 text-left">Unit Price</th>
                      <th className="px-4 py-3 text-left">Cost</th>
                      <th className="px-4 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {poDetails.map((detail, idx) => (
                      <tr key={detail.id} className="hover:bg-blue-50 transition-all">
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            disabled
                            value={detail.itemDescription || ''}
                            className="w-full px-2 py-1 border border-gray-200 rounded-lg bg-gray-50 text-sm cursor-not-allowed"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            disabled
                            value={detail.qty || ''}
                            className="w-20 px-2 py-1 border border-gray-200 rounded-lg bg-gray-50 text-sm cursor-not-allowed"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            disabled
                            value={detail.uom || ''}
                            className="w-20 px-2 py-1 border border-gray-200 rounded-lg bg-gray-50 text-sm cursor-not-allowed"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            value={detail.unitPrice || ''}
                            onChange={(e) => {
                              const price = parseFloat(e.target.value) || 0;
                              const cost = price * (detail.qty || 0);
                              const updated = [...poDetails];
                              updated[idx] = { ...detail, unitPrice: price, costEstimate: cost };
                              setPoDetails(updated);
                            }}
                            className="w-32 px-2 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-bold text-green-600">
                              ₱{((detail.unitPrice || 0) * (detail.qty || 0)).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                            </span>
                            {editFormData.isVatInclusive && (
                              <span className="text-xs text-blue-500">
                                +VAT ₱{((detail.unitPrice || 0) * (detail.qty || 0) * 0.12).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleDeleteDetail(detail.id)}
                            className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Grand Total */}
              <div className="mt-3 flex justify-end">
                <div className="bg-gray-50 border border-gray-200 rounded-xl px-6 py-3 text-right space-y-1">
                  {(() => {
                    const subtotal = poDetails.reduce((sum, d) => sum + (d.unitPrice || 0) * (d.qty || 0), 0);
                    const vat = editFormData.isVatInclusive ? subtotal * 0.12 : 0;
                    const total = subtotal + vat;
                    return (
                      <>
                        <p className="text-sm text-gray-500">Subtotal (VAT-Ex): <span className="font-bold text-gray-800">₱{subtotal.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></p>
                        {editFormData.isVatInclusive && (
                          <p className="text-sm text-blue-500">VAT (12%): <span className="font-bold">₱{vat.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</span></p>
                        )}
                        <p className="text-base font-extrabold text-green-600">Total: ₱{total.toLocaleString('en-PH', { minimumFractionDigits: 2 })}</p>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="p-6 border-t flex gap-3">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
          >
            <Save size={20} />
            Save Changes
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-bold hover:bg-gray-300 transition-all"
          >
            <X size={20} />
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Detail Edit Card Component
function DetailEditCard({ detail, onUpdate, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(detail);

  const handleSave = () => {
    onUpdate(detail.id, formData);
    setIsEditing(false);
  };

  return (
    <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-xl border border-gray-200">
      {isEditing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Item Description"
              disabled
              value={formData.itemDescription || ''}
              onChange={(e) => setFormData({ ...formData, itemDescription: e.target.value })}
              className="md:col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              placeholder="Qty"
              value={formData.qty || ''}
              disabled
              onChange={(e) => setFormData({ ...formData, qty: parseFloat(e.target.value) })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="text"
              placeholder="UOM"
              value={formData.uom || ''}
              disabled
              onChange={(e) => setFormData({ ...formData, uom: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              placeholder="Unit Price"
              value={formData.unitPrice || ''}
              onChange={(e) => {
                const price = parseFloat(e.target.value) || 0;
                const total = price * (formData.qty || 0);
                setFormData({ ...formData, unitPrice: price, costEstimate: total });
              }} className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              placeholder="Discount %"
              value={formData.detailDiscount || ''}
              onChange={(e) => setFormData({ ...formData, detailDiscount: parseFloat(e.target.value) })}
              disabled
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <input
              type="number"
              placeholder="Cost Estimate"
              value={formData.costEstimate || ''}
              onChange={(e) => setFormData({ ...formData, costEstimate: parseFloat(e.target.value) })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              className="flex items-center gap-1 bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-600"
            >
              <Save size={16} />
              Save
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="flex items-center gap-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-400"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 flex-1">
            <div className="md:col-span-2">
              <p className="text-xs text-gray-500">Item</p>
              <p className="text-sm font-semibold">{detail.itemDescription}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Qty</p>
              <p className="text-sm font-semibold">{detail.qty} {detail.uom}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Unit Price</p>
              <p className="text-sm font-semibold">₱{detail.unitPrice?.toFixed(2)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Discount</p>
              <p className="text-sm font-semibold">{detail.detailDiscount}%</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Cost</p>
              <p className="text-sm font-semibold text-green-600">₱{detail.costEstimate?.toFixed(2)}</p>
            </div>
          </div>
          <div className="flex gap-2 ml-4">
            <button
              onClick={() => setIsEditing(true)}
              className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              <Edit2 size={16} />
            </button>
            <button
              onClick={() => onDelete(detail.id)}
              className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}